// src/utils/factsManager.ts

import { RETINOL_FACTS, type RetinolFact } from '../data/messages';
import { logger } from './logger';

const STORAGE_KEY = 'retinol_facts_history';
const MAX_HISTORY_DAYS = 30;

interface FactHistory {
[dateKey: string]: number; // дата -> индекс факта
}

/**
 * Форматирует дату в строку YYYY-MM-DD
 */
const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Загружает историю показанных фактов из localStorage
 */
const loadHistory = (): FactHistory => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    
    const parsed = JSON.parse(stored);
    
    // Валидация: проверяем, что это объект с числовыми значениями
    if (typeof parsed !== 'object' || parsed === null) {
      logger.warn('Invalid facts history format, resetting');
      return {};
    }
    
    // Фильтруем невалидные записи
    const validated: FactHistory = {};
    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value === 'number' && value >= 0 && value < RETINOL_FACTS.length) {
        validated[key] = value;
      }
    });
    
    return validated;
  } catch (error) {
    logger.error('Failed to load facts history:', error);
    return {};
  }
};

/**
 * Сохраняет историю показанных фактов в localStorage
 */
const saveHistory = (history: FactHistory): void => {
  try {
    // Очищаем старые записи (оставляем только последние MAX_HISTORY_DAYS дней)
    const sortedDates = Object.keys(history).sort().reverse();
    const cleanedHistory: FactHistory = {};
    
    sortedDates.slice(0, MAX_HISTORY_DAYS).forEach(date => {
      cleanedHistory[date] = history[date];
    });
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedHistory));
  } catch (error) {
    logger.error('Failed to save facts history:', error);
  }
};

/**
 * Выбирает наименее показанный факт с учётом истории
 */
const selectLeastShownFact = (history: FactHistory): number => {
  // Подсчитываем, сколько раз показывали каждый факт
  const factCounts = new Array(RETINOL_FACTS.length).fill(0);
  
  Object.values(history).forEach(factIndex => {
    if (factIndex >= 0 && factIndex < RETINOL_FACTS.length) {
      factCounts[factIndex]++;
    }
  });
  
  // Находим минимальное количество показов
  const minCount = Math.min(...factCounts);
  
  // Собираем индексы фактов с минимальным количеством показов
  const leastShownIndices: number[] =[];
  factCounts.forEach((count, index) => {
    if (count === minCount) {
      leastShownIndices.push(index);
    }
  });
  
  // Выбираем случайный из наименее показанных
  const randomIndex = Math.floor(Math.random() * leastShownIndices.length);
  return leastShownIndices[randomIndex];
};

/**
 * Получает факт дня с умной ротацией
 * @param date - дата, для которой нужно получить факт (по умолчанию - сегодня)
 * @returns объект с фактом
 */
export const getFactOfTheDay = (date: Date = new Date()): RetinolFact => {
  try {
    const dateKey = formatDateKey(date);
    const history = loadHistory();
    
    // Проверяем, показывали ли уже факт сегодня
    const todayFactIndex = history[dateKey];
    
    if (todayFactIndex !== undefined && 
        todayFactIndex >= 0 && 
        todayFactIndex < RETINOL_FACTS.length) {
      logger.info(`Using cached fact for ${dateKey}: index ${todayFactIndex}`);
      return RETINOL_FACTS[todayFactIndex];
    }
    
    // Выбираем новый факт
    const selectedIndex = selectLeastShownFact(history);
    
    // Сохраняем выбор
    history[dateKey] = selectedIndex;
    saveHistory(history);
    
    logger.info(`Selected new fact for ${dateKey}: index ${selectedIndex}`);
    return RETINOL_FACTS[selectedIndex];
    
  } catch (error) {
    logger.error('Error in getFactOfTheDay:', error);
    // Возвращаем первый факт как fallback
    return RETINOL_FACTS[0];
  }
};

/**
 * Получает случайный факт, отличный от текущего
 * @param currentFactText - текст текущего факта
 * @returns новый факт
 */
export const getRandomDifferentFact = (currentFactText: string): RetinolFact => {
  try {
    // Находим индекс текущего факта
    const currentIndex = RETINOL_FACTS.findIndex(fact => fact.text === currentFactText);
    
    // Если фактов только один или текущий не найден, возвращаем случайный
    if (RETINOL_FACTS.length <= 1 || currentIndex === -1) {
      const randomIndex = Math.floor(Math.random() * RETINOL_FACTS.length);
      return RETINOL_FACTS[randomIndex];
    }
    
    // Выбираем случайный факт, отличный от текущего
    let newIndex: number;
    do {
      newIndex = Math.floor(Math.random() * RETINOL_FACTS.length);
    } while (newIndex === currentIndex);
    
    logger.info(`Selected random fact: ${newIndex} (previous was ${currentIndex})`);
    return RETINOL_FACTS[newIndex];
    
  } catch (error) {
    logger.error('Error in getRandomDifferentFact:', error);
    // Возвращаем первый факт как fallback
    return RETINOL_FACTS[0];
  }
};

/**
 * Очищает историю фактов (для отладки или сброса)
 */
export const clearFactsHistory = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    logger.info('Facts history cleared');
  } catch (error) {
    logger.error('Failed to clear facts history:', error);
  }
};